import React, { useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Button from '@ui/Button';
import Icon from '@ui/Icon';
import Typography from '@ui/Typography';
import StudioToolbarTooltip from '../ToolbarDrawer/StudioToolbarTooltip';
import useCuesModeError, {
  CuesModeErrorText,
} from '../ToolbarDrawer/useCuesModeError';
import {
  EventName,
  useEventsContext,
} from '../../../context/events/EventsContext';
import {
  useStudioClipPreviewContext,
  useStudioClipPreviewListContext,
} from '../../../context/previewClip/StudioPreviewClipContext';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { EditMode, undo, redo } from '../../../store/EditorSlice';
import { clearCues } from '../../../store/CuesSlice';

export const ID_STUDIO_UTILITY_ACTIONS = 'studio-utility-actions';
const HIDE_CLASS = 'hide';
const WHITE_ALPHA_12 = '#FFFFFF1F';

const useStyles = makeStyles()(theme => ({
  root: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gridTemplateAreas: '"left center right"',
    marginBottom: theme.spacing(2.5),
  },
  leftUtilities: {
    gridArea: 'left',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  rightUtilities: {
    gridArea: 'right',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    opacity: 1,
    visibility: 'visible',
    transition: theme.transitions.create(['opacity', 'visibility'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.short,
    }),
    [`&.${HIDE_CLASS}`]: {
      opacity: 0,
      visibility: 'hidden',
      pointerEvents: 'none',
    },
  },
  utilityButton: {
    height: 'auto',
    '&:hover': {
      backgroundColor: WHITE_ALPHA_12,
    },
  },
  tabs: {},
  tab: {
    alignItems: 'center',
    color: palette.gray_75,
    display: 'flex',
    justifyContent: 'center',
    margin: 0,
    marginRight: theme.spacing(1),
    minWidth: 'unset',
    paddingBottom: theme.spacing(2),
    width: '78px',
    '& .MuiTab-wrapper': {
      justifyContent: 'center',
    },
    '&:hover': {
      background: WHITE_ALPHA_12,
      color: palette.gray_0,
      textDecoration: 'none',
    },
    '&:focus': {
      border: 'none',
      color: 'transparent',
    },
    '&:focus-visible': {
      border: `1px solid ${palette.mint_200}`,
      color: 'transparent',
    },
  },
  rightTab: {},
  tabLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    textTransform: 'capitalize',
    '& > :first-child': {
      marginRight: theme.spacing(1),
    },
  },
}));

interface StudioModeTabsProps {
  onTabChange: (mode: EditMode) => void;
  disableScriptTabChange?: boolean | null;
  setIsNavigationConfirmationModalOpen: (isOpen: boolean) => void;
}

const StudioModeTabs = ({
  onTabChange,
  disableScriptTabChange,
  setIsNavigationConfirmationModalOpen,
}: StudioModeTabsProps) => {
  const { classes, cx } = useStyles();
  const editMode = useAppSelector(state => state.editor.editMode);
  const { errorMessage } = useCuesModeError();
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [eventTracking] = useEventsContext();
  const { setClip } = useStudioClipPreviewContext();
  const { setHasCueChanged, resetPreviews } = useStudioClipPreviewListContext();

  /**
   * Render the error message with the component
   * but if no error message, the underlying tooltip component
   * accepts an empty string as a way to hide the tooltip
   */
  const tooltipText = errorMessage ? (
    <CuesModeErrorText text={errorMessage} />
  ) : (
    ''
  );

  const handleScriptTabClick = () => {
    if (disableScriptTabChange) {
      setIsNavigationConfirmationModalOpen(true);
    } else {
      onTabChange(EditMode.SCRIPT);
      if (setClip) setClip(undefined);
      if (resetPreviews) resetPreviews();
    }
  };

  const handleCuesTabClick = () => {
    if (!errorMessage) {
      if (onTabChange) {
        onTabChange(EditMode.CUES);
      }
      if (setHasCueChanged) setHasCueChanged(true);
    } else {
      setShowTooltip(true);
      eventTracking.track({
        type: EventName.CuesDisabled,
        data: {
          action: 'tabClick',
        },
      });
    }
  };

  const hideTooltip = () => setShowTooltip(false);

  return (
    <Tabs className={classes.tabs} value={editMode}>
      <Tab
        tabIndex={0}
        className={classes.tab}
        component="a"
        value={EditMode.SCRIPT}
        label={
          <Typography variant="h5" bold className={classes.tabLabel}>
            <Icon type="script" /> script
          </Typography>
        }
        onClick={handleScriptTabClick}
      />
      <Tab
        tabIndex={0}
        className={cx(classes.tab, classes.rightTab)}
        // This is needed to override pointer events none which is set
        // if tab is disabled.
        style={{ pointerEvents: 'auto' }}
        component="a"
        value={EditMode.CUES}
        label={
          <StudioToolbarTooltip title={tooltipText} open={showTooltip}>
            <Box display="flex" justifyContent="center" width="100%">
              <Typography variant="h5" bold className={classes.tabLabel}>
                <Icon type="cues" /> cues
              </Typography>
            </Box>
          </StudioToolbarTooltip>
        }
        disabled={Boolean(errorMessage)}
        aria-disabled={Boolean(errorMessage)}
        onClick={handleCuesTabClick}
        onMouseLeave={hideTooltip}
      />
    </Tabs>
  );
};

interface StudioUtilityBarProps extends StudioModeTabsProps {}

const StudioNavbar = ({
  onTabChange,
  disableScriptTabChange,
  setIsNavigationConfirmationModalOpen,
}: StudioUtilityBarProps) => {
  const dispatch = useAppDispatch();
  const { canUndo, canRedo } = useAppSelector(state => state.editor.meta);
  const { editMode, projectId } = useAppSelector(state => state.editor);
  const isCuesMode = editMode === EditMode.CUES;
  const { classes, cx } = useStyles();
  const [eventTracking] = useEventsContext();
  const trackingData = { projectId: projectId || '' };

  const onClearAllCues = () => {
    dispatch(clearCues(null));
    eventTracking.track({
      type: EventName.ClearAllCues,
      data: trackingData,
    });
  };

  const onUndo = () => {
    dispatch(undo(null));
    eventTracking.track({
      type: EventName.EditorUndoPressed,
      data: trackingData,
    });
  };

  const onRedo = () => {
    dispatch(redo(null));
    eventTracking.track({
      type: EventName.EditorRedoPressed,
      data: trackingData,
    });
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.leftUtilities}>
        <StudioModeTabs
          onTabChange={onTabChange}
          disableScriptTabChange={disableScriptTabChange}
          setIsNavigationConfirmationModalOpen={
            setIsNavigationConfirmationModalOpen
          }
        />
      </Box>
      <Box
        className={cx(classes.rightUtilities, !isCuesMode && HIDE_CLASS)}
        id={ID_STUDIO_UTILITY_ACTIONS}
      >
        <Button
          variant="icon_text"
          color="secondary"
          iconType="clear"
          className={classes.utilityButton}
          style={{ whiteSpace: 'nowrap', textTransform: 'none' }}
          onClick={onClearAllCues}
        >
          Clear all cues
        </Button>
        <Button
          variant="icon_text"
          color="secondary"
          iconType="reverse-left"
          className={classes.utilityButton}
          onClick={onUndo}
          disabled={!canUndo}
        >
          undo
        </Button>
        <Button
          variant="icon_text"
          color="secondary"
          iconType="reverse-right"
          className={classes.utilityButton}
          onClick={onRedo}
          disabled={!canRedo}
        >
          redo
        </Button>
      </Box>
    </Box>
  );
};

export default StudioNavbar;
